import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as RejectIcon } from '../../assets/icons/close-circle-red.svg';
import { ReactComponent as DocumentFile } from '../../assets/icons/document-file.svg';
import { ReactComponent as EmailIcon } from '../../assets/icons/email-icon.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as PhoneIcon } from '../../assets/icons/phone-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { ReactComponent as VerifyIcon } from '../../assets/icons/tick-circle-icon.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user-icon.svg';
import { ReactComponent as UserTagIcon } from '../../assets/icons/user-tag.svg';
import profiles from '../../assets/images/profiles/profile.jpg';
import {
  AccessFacilityPortal,
  AccountsInterFace,
} from '../../interface/account-management.interface';
import RejectAccount from '../../pages/account-management/components/reject-account';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllAccounts, fetchAllAccountsId } from '../../redux/slices/AccountManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { RootState } from '../../redux/store';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem } from '../../shared-functions/LocalStorage';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import { checkFileSizeValidation } from '../../shared-functions/Validation';
import accountApi from '../../utils/api/account';
import accountv2Api from '../../utils/api/v2/accounts';
import auth from '../../utils/api/v2/auth';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomFileInput from '../custom-file-input/CustomFileInput';
import CustomInput from '../custom-input/CustomInput';
import CustomPhoneInput from '../custom-input/CustomPhoneInput';
import FormCancelWarning from '../form-cancel-warning/FormCancelWarning';
import ConfirmBox from '../modal-box/ConfirmBox';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';
import TabToggle from '../tab-toggle/TabToggle';

interface FacilityDetailsSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
  actionActive?: boolean;
  onBack?: () => void;
  hideHeader?: boolean;
  onSuccess?: () => void;
  /** Optional callback for the parent to refresh its own list. When provided, the internal AccountManagementSlice refresh is skipped. */
  onRefreshList?: () => void;
}

/**
 * Open the document in new tap function
 * @param {string} pdfUrl - pdf url
 */
const openPdf = (pdfUrl: string) => {
  if (pdfUrl) window.open(pdfUrl, '_blank');
};

/**
 * Resolves existing government ID file items into a valid array.
 * @param {any} govId - The government ID field value from account data.
 * @returns {any[]} An array of valid file objects or empty array.
 */
const resolveGovernmentIdFiles = (govId: any): any[] => {
  if (!govId) return [];
  if (Array.isArray(govId)) {
    return govId.filter((item) => {
      if (!item) return false;
      if (typeof item === 'string') return item.trim().length > 0;
      if (typeof item === 'object') {
        return (
          Boolean(item.signedUrl) ||
          Boolean(item.url) ||
          Boolean(item.fileName) ||
          Boolean(item.name) ||
          Boolean(item.path)
        );
      }
      return false;
    });
  }
  if (typeof govId === 'string' && govId.trim().length > 0) {
    return [{ signedUrl: govId, fileName: govId }];
  }
  if (typeof govId === 'object') {
    if (govId.signedUrl || govId.url || govId.fileName || govId.name || govId.path) {
      return [govId];
    }
  }
  return [];
};

/**
 * Facility details side panel component
 * @param {Readonly<FacilityDetailsSidePanelProperties>} properties - properties
 * @returns {React.ReactElement} component
 */
function FacilityDetailsSidePanel(properties: Readonly<FacilityDetailsSidePanelProperties>) {
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const { onEditModeChange, fetchIdFrom, id, onBack, isEditMode, onSuccess, onRefreshList } =
    properties;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const location = useLocation();
  const { accounts, page, limit } = useAppSelector((state: RootState) => state.accounts);
  const [accountData, setAccountData] = useState<AccountsInterFace | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Modals state
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [editMode, setEditMode] = useState(isEditMode || false);
  const [editData, setEditData] = useState<any>({}); // Use specific type if possible, or AccountsInterFace partial
  const [isInitialized, setIsInitialized] = useState(false);
  const [detailsFetched, setDetailsFetched] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Confirm submit function
     */
    conformSubmit: () => {},
  });

  /**
   * Show success notification
   * @param {string} message - message
   * @param {() => void} [onCloseCallback] - The onCloseCallback
   */
  const showSuccessNotification = (message: string, onCloseCallback?: () => void) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: true,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setModalView((previous) => ({ ...previous, status: false }));
            onCloseCallback?.();
          }}
        />
      ),
    });
  };

  /**
   * Fetch facility details from API
   * @param {string} facilityId - facility id
   */
  const fetchDetails = (facilityId: string) => {
    setDetailsLoading(true);
    setDetailsFetched(false);

    const existingAccountFromRedux = accounts.find(
      (item: AccountsInterFace) =>
        item.id === facilityId || item._id === facilityId || item.accountCustomId === facilityId,
    );

    Promise.allSettled([
      accountApi.fetchOneAccounts(facilityId),
      accountApi.fetchAccountDetails(facilityId),
    ])
      .then(([userResult, detailsResult]) => {
        const userData = userResult.status === 'fulfilled' ? userResult.value?.data || {} : {};
        const detailsData =
          detailsResult.status === 'fulfilled' ? detailsResult.value?.data || {} : {};

        setAccountData((previous: AccountsInterFace | null) => {
          const mergedData = {
            ...existingAccountFromRedux,
            ...previous,
            ...userData,
            ...detailsData,
          };

          const stationCount =
            mergedData.stationCount ??
            mergedData.noOfStations ??
            mergedData.totalStations ??
            existingAccountFromRedux?.stationCount;
          const totalDeviceCount =
            mergedData.totalDeviceCount ??
            (mergedData as any).noOfDevices ??
            mergedData.noOfChargers ??
            mergedData.totalChargers ??
            existingAccountFromRedux?.totalDeviceCount;
          const activeDeviceCount =
            mergedData.activeDeviceCount ??
            mergedData.onlineChargers ??
            existingAccountFromRedux?.activeDeviceCount;
          const powerConsumed =
            mergedData.powerConsumed ??
            mergedData.kwhConsumed ??
            existingAccountFromRedux?.powerConsumed;

          return {
            ...mergedData,
            stationCount,
            totalDeviceCount,
            activeDeviceCount,
            powerConsumed,
          };
        });
        setDetailsLoading(false);
        setDetailsFetched(true);
      })
      .catch((error: any) => {
        setAccountData(existingAccountFromRedux || null);
        setDetailsLoading(false);
        setDetailsFetched(true);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('failedToFetchDetails'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  // Get active facility ID
  const activeFacilityId =
    fetchIdFrom === 'queryParams'
      ? searchParameters.get('facilityId') || searchParameters.get('accountCustomId') || undefined
      : id;

  // Initial load details when activeFacilityId changes
  useEffect(() => {
    if (activeFacilityId) {
      setActiveTab('overview');
      fetchDetails(activeFacilityId);
    }
  }, [activeFacilityId]);

  useEffect(() => {
    const currentAction = searchParameters.get('action');
    const isEditActive = isEditMode || currentAction === 'edit';
    setEditMode(isEditActive || false);

    if (isEditActive) {
      if (accountData && detailsFetched && !isInitialized) {
        setEditData({
          accountName: accountData.accountName || '',
          name: accountData.name || '',
          email: accountData.email || '',
          phoneNumber: accountData.phoneNumber || '',
          countryCode: accountData.countryCode || '1',
          governmentIdOld: resolveGovernmentIdFiles((accountData as any)?.governmentId),
        });
        setIsInitialized(true);
      }
    } else {
      setEditData({});
      setIsInitialized(false);
    }
  }, [isEditMode, searchParameters, accountData, detailsFetched, isInitialized]);

  /**
   * Refresh the accounts list.
   * If the parent provides `onRefreshList`, delegate to it (e.g. revenue management uses a different slice).
   * Otherwise fall back to the default AccountManagementSlice fetch.
   */
  const refreshList = () => {
    if (onRefreshList) {
      onRefreshList();
    } else {
      dispatch(fetchAllAccounts({ page, limit }));
    }
    dispatch(fetchAllAccountsId());
  };

  /**
   * Handle Verify Account
   */
  const handleVerify = () => {
    if (!accountData?.accountCustomId) return;

    setConfirmBox({
      status: true,
      title: t('verifyAccountTitle'),
      subTitle: t('verifyAccountSubTitle'),
      submitLabel: t('verify'),
      /**
       * Confirm submit function
       */
      conformSubmit: () => {
        accountv2Api
          .approvedAccounts(accountData.accountCustomId, { submissionStatus: 'approved' })
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('accountApprovedSuccessfully'), () => {
              if (!onSuccess) refreshList();
              onSuccess?.();
              // update local state
              setAccountData((previous: AccountsInterFace | null) =>
                previous
                  ? {
                      ...previous,
                      status: 'approved',
                      submission: { ...previous.submission, status: 'approved' },
                    }
                  : null,
              );
            });
            // Also wait a bit then close the panel or just refresh
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: 0,
              }),
            );
          });
      },
    });
  };

  /**
   * Handle Reject Account
   */
  const handleReject = () => {
    if (!accountData) return;

    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <RejectAccount
          accountDetails={{
            accountName: accountData.accountName || '',
            status: accountData.status,
            name: accountData.name,
            governmentId: [],
            email: accountData.email,
            phone: accountData.phoneNumber || '',
            address: accountData.address || '',
            password: '',
            countryCode: accountData.countryCode || '',
            rePassword: '',
          }}
          closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
          conformSubmit={(value: any) => {
            if (accountData.accountCustomId) {
              accountv2Api
                .approvedAccounts(accountData.accountCustomId, {
                  reason: value.reason,
                  submissionStatus: 'rejected',
                })
                .then(() => {
                  setModalView((previous) => ({ ...previous, status: false }));
                  showSuccessNotification(t('accountRejectedSuccessfully'), () => {
                    if (!onSuccess) refreshList();
                    onSuccess?.();
                    setAccountData((previous: AccountsInterFace | null) =>
                      previous
                        ? {
                            ...previous,
                            status: 'rejected',
                            submission: { ...previous.submission, status: 'rejected' },
                          }
                        : null,
                    );
                  });
                })
                .catch((error: any) => {
                  dispatch(
                    setNotificationMessage({
                      message: error?.response?.data?.message,
                      status: true,
                      type: 'error',
                      code: 0,
                    }),
                  );
                });
            }
          }}
        />
      ),
    });
  };

  /**
   * Handle Reset Password
   */
  const handleResetPassword = () => {
    if (!accountData?.accountCustomId) return;

    setConfirmBox({
      status: true,
      title: t('resetPasswordTitle'),
      subTitle: t('confirmResetSubtitle'),
      submitLabel: t('proceed'),
      /**
       * Conform submit function
       */
      conformSubmit: () => {
        accountApi
          .facilityRestPassword(accountData.accountCustomId, { submissionStatus: 'resetPassword' })
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('passwordResetLinkSent'));
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: 0,
              }),
            );
          });
      },
    });
  };

  /**
   * View Facility Function
   * @param {any} [currentData] - Account custom ID
   * @returns {void} The result
   */
  const viewFacilityFunction = (currentData?: any) => {
    const targetCustomId = currentData || accountData?.accountCustomId;
    if (targetCustomId) {
      const request: AccessFacilityPortal = {
        accountCustomId: targetCustomId,
      };

      auth
        .accessFacilityPortal(request)
        .then((response) => {
          const responseData = response?.data;
          const { origin } = window;

          const url = new URL(origin);

          if (url.hostname.includes('admin')) {
            url.hostname = url.hostname.replace('admin', 'facility');
          } else if (url.hostname.includes('cpo')) {
            url.hostname = url.hostname.replace('cpo', 'partner');
          } else if (url.hostname.includes('networks')) {
            url.hostname = url.hostname.replace('networks', 'partner');
          } else if (url.hostname.includes('app')) {
            url.hostname = url.hostname.replace('app', 'partner');
          }

          const updatedOrigin = url.origin;
          const clientName = getLocalStorageItem('client-name');
          const currentLanguage = i18n.language || 'en';
          const partnerName =
            accountData?.accountName ||
            accounts.find(
              (item: AccountsInterFace) =>
                item.accountCustomId === targetCustomId ||
                item.id === targetCustomId ||
                item._id === targetCustomId,
            )?.accountName ||
            '';

          window.open(
            `${updatedOrigin}/login/credentials?accessToken=${responseData.access.token}&accessTokenExpires=${responseData.access.expires}&isEmailVerified=${responseData.isEmailVerified}&isPhoneNumberVerified=${responseData.isPhoneNumberVerified}&isInitialPasswordReset=${responseData.isInitialPasswordReset}&accountVerificationStatus=${responseData.accountVerificationStatus}&clientName=${clientName}&lng=${encodeURIComponent(currentLanguage)}&partnerName=${encodeURIComponent(partnerName)}&accountCustomId=${encodeURIComponent(targetCustomId)}`,
            '_blank',
          );
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (editMode) {
      // Cancelling edit mode
      setEditMode(false);
      setEditData({});
      if (onEditModeChange) onEditModeChange(false);
    } else {
      // Entering edit mode - initialize data
      setEditData({
        accountName: accountData?.accountName || '',
        name: accountData?.name || '',
        email: accountData?.email || '',
        phoneNumber: accountData?.phoneNumber || '',
        countryCode: accountData?.countryCode || '1',
        governmentIdOld: resolveGovernmentIdFiles((accountData as any)?.governmentId),
      });
      setEditMode(true);
      if (onEditModeChange) onEditModeChange(true);
    }
  };

  /**
   * Handle Input Change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    if (name === 'governmentId') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
        governmentIdOld: [],
      }));
    } else {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  /**
   * Handle unselecting/removing a file from governmentId or governmentIdOld
   * @param {string} name - The input field name
   * @param {string} url - The file url
   * @param {number} indexToRemove - The index of the file to remove
   * @param {'newFile' | 'existingFile'} type - The file type ('newFile' or 'existingFile')
   */
  const handleRemoveFile = (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setEditData((previous: any) => ({
        ...previous,
        governmentId: (previous.governmentId || []).filter(
          (_: any, index: number) => index !== indexToRemove,
        ),
      }));
    } else if (type === 'existingFile') {
      setEditData((previous: any) => ({
        ...previous,
        governmentIdOld: (previous.governmentIdOld || []).filter(
          (_: any, index: number) => index !== indexToRemove,
        ),
      }));
    }
  };

  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    if (!accountData?.accountCustomId) return;
    // Prepare payload - handle file upload separately if needed or as part of payload
    // Assuming editAccounts handles the payload structure
    if (
      editData.governmentId?.length > 0 &&
      !checkFileSizeValidation(editData.governmentId, 1, 1)
    ) {
      return;
    }
    const payload: any = {};
    if (editData?.name) {
      payload.name = editData?.name;
    }
    if (editData?.accountName) {
      payload.accountName = editData?.accountName;
    }
    if (editData?.email) {
      payload.email = editData?.email;
    }
    if (editData?.phoneNumber && editData?.countryCode) {
      payload.phoneNumber = editData?.phoneNumber;
      payload.countryCode = editData?.countryCode;
    }
    if (editData?.governmentId?.[0]) {
      payload.governmentId = editData?.governmentId?.[0];
    }

    // If governmentId is an array of files/objects, backend might expect a specific format
    // Adjust payload logic based on API requirements.
    // Here we'll pass it as is or handle file upload logic if your createAccounts does it.
    // For now, assuming straightforward update.

    // NOTE: If file upload logic is complex (like uploading first to get URL), add that here.
    // CustomFileInput returns File[] usually.

    setSubmitLoading(true);
    accountApi
      .editAccounts(accountData.accountCustomId, payload)
      .then(() => {
        setSubmitLoading(false);
        showSuccessNotification(t('accountUpdatedSuccessfully'), () => {
          setEditMode(false);
          if (onEditModeChange) onEditModeChange(false);
          if (!onSuccess) refreshList();
          onSuccess?.();
          // Optimistically update local data or re-fetch
          fetchDetails(accountData.accountCustomId);
          dispatch(fetchAllAccountsId());
        });
      })
      .catch((error: any) => {
        setSubmitLoading(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  if (detailsLoading) {
    return (
      <div className="loader">
        <BatteryLoader />
      </div>
    );
  }

  // if (!accountData && !detailsLoading)
  //   return (
  //     <div className="facility-details-side-panel">
  //       {!hideHeader && (
  //         <div className="panel-header">
  //           {(onBack || location.state?.fromStationId) && (
  //             <div className="edit-close">
  //               <BackArrowIcon
  //                 onClick={() => {
  //                   if (location.state?.fromStationId) {
  //                     const parameters = new URLSearchParams(searchParameters);
  //                     parameters.delete('accountCustomId');
  //                     parameters.set('stationId', location.state.fromStationId);
  //                     setSearchParameters(parameters, { state: {} });
  //                   } else if (onBack) {
  //                     onBack();
  //                   }
  //                 }}
  //               />
  //             </div>
  //           )}
  //         </div>
  //       )}
  //       <p className="not-found">{t('noDataFound')}</p>
  //     </div>
  //   );

  /**
   * Handle back navigation
   * @param {boolean} forceClose - When true, skips the edit-mode guard and closes the panel even if editMode is still true (used after a confirmed cancel).
   */
  const handleBack = (forceClose = false) => {
    if (editMode && !forceClose) {
      toggleEditMode();
      return;
    }
    if (location.state?.fromStationId) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('accountCustomId');
      parameters.set('stationId', location.state.fromStationId);
      setSearchParameters(parameters, { state: {} });
      return;
    }
    if (onBack) {
      onBack();
    }
  };

  /**
   * Handle cancel edit click - warns the user that unsaved changes will be discarded
   * before actually leaving edit mode.
   */
  const handleCancelEdit = () => {
    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: true,
      content: (
        <FormCancelWarning
          title={t('cancelTitle')}
          description={t('cancelDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalView((previous) => ({ ...previous, status: false }))}
          submitButtonLabel={t('ok')}
          submitButtonOnClick={() => {
            setModalView((previous) => ({ ...previous, status: false }));
            setEditMode(false);
            setEditData({});
            if (onEditModeChange) onEditModeChange(false);
            handleBack(true);
          }}
        />
      ),
    });
  };

  return (
    <div className="facility-details-side-panel" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />
      <div className="profile-card">
        <div className="profile-card-content">
          <div>
            <img
              src={(accountData as any)?.profileImage?.signedUrl || profiles}
              alt={t('profile')}
            />
            <div className="profile-info">
              <div className="name">
                <span>{accountData?.accountName || '-'}</span>
                {accountData?.submission?.status === 'approved' && <VerifyIcon />}
                {accountData?.submission?.status === 'rejected' && <RejectIcon />}
              </div>
              <div className="id">{accountData?.accountCustomId}</div>
            </div>
          </div>
          <div>
            <MyButton
              label={t('accessPartner')}
              buttonType="submit"
              svgBackIcon={<NewTabIcon />}
              onClickFunc={() => viewFacilityFunction(searchParameters.get('accountCustomId'))}
              disableButton={!permissionCheck('cpoPartnerControlAccess')}
            />
          </div>
        </div>
      </div>
      <div className="tab-toggle-container">
        <TabToggle
          tabs={[
            { label: t('overview'), value: 'overview' },
            { label: t('stations'), value: 'stations' },
            { label: t('documents'), value: 'documents' },
          ]}
          activeTab={activeTab}
          defaultTab="overview"
          onChange={(value) => setActiveTab(value)}
          isLabelValue
        />
      </div>
      <div className="facility-details-body">
        {activeTab === 'overview' && (
          <div className="facility-details-group">
            <div className="facility-button">
              <div className="section basic-info">
                <h4>{t('basicInformation')}</h4>
                {editMode ? (
                  <>
                    <div className="info-row-input">
                      <p className="label">{t('accountName')}:</p>
                      <CustomInput
                        type="text"
                        name="accountName"
                        placeholder={t('accountName')}
                        value={editData.accountName}
                        onChangeValue={handleInputChange}
                        // inputLabel="accountName"
                      />
                    </div>
                    <div className="info-row-input">
                      <p className="label">{t('userName')}:</p>
                      <CustomInput
                        type="text"
                        name="name"
                        placeholder={t('userName')}
                        value={editData.name}
                        onChangeValue={handleInputChange}
                      />
                    </div>
                    <div className="info-row-input">
                      <p className="label">{t('email')}:</p>
                      <CustomInput
                        type="email"
                        name="email"
                        placeholder={t('email')}
                        value={editData.email}
                        onChangeValue={handleInputChange}
                      />
                    </div>
                    <div className="info-row-input">
                      <p className="label">{t('phoneNumber')}:</p>
                      <CustomPhoneInput
                        type="number"
                        name="phoneNumber"
                        placeholder={t('phoneNumber')}
                        value={editData?.phoneNumber}
                        value1={editData?.countryCode}
                        onChangeValue={handleInputChange}
                      />
                    </div>
                    <div className="basic-info-grid">
                      <div className="info-row">
                        <span className="icon-wrapper">
                          <CalendarIcon />
                        </span>
                        <span className="label">{t('createdAt')}</span>
                        <span className="colon">:</span>
                        <span className="value">
                          {accountData?.createdAt
                            ? ConvertUTCtoDateTime(accountData?.createdAt)
                            : '-'}
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="icon-wrapper">
                          <ClockIcon />
                        </span>
                        <span className="label">{t('updatedAt')}</span>
                        <span className="colon">:</span>
                        <span className="value">
                          {accountData?.updatedAt
                            ? ConvertUTCtoDateTime(accountData?.updatedAt)
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="basic-info-grid">
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <UserTagIcon />
                      </span>
                      <span className="label">{t('accountName')}</span>
                      <span className="colon">:</span>
                      <span className="value">{accountData?.accountName || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <UserIcon />
                      </span>
                      <span className="label">{t('userName')}</span>
                      <span className="colon">:</span>
                      <span className="value">{accountData?.name || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <EmailIcon />
                      </span>
                      <span className="label">{t('email')}</span>
                      <span className="colon">:</span>
                      <span className="value">{accountData?.email || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <PhoneIcon />
                      </span>
                      <span className="label">{t('phone')}</span>
                      <span className="colon">:</span>
                      <span className="value">
                        {accountData?.countryCode ? `+${accountData?.countryCode} ` : ''}
                        {accountData?.phoneNumber || '-'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <CalendarIcon />
                      </span>
                      <span className="label">{t('createdAt')}</span>
                      <span className="colon">:</span>
                      <span className="value">
                        {accountData?.createdAt
                          ? ConvertUTCtoDateTime(accountData?.createdAt)
                          : '-'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="icon-wrapper">
                        <ClockIcon />
                      </span>
                      <span className="label">{t('updatedAt')}</span>
                      <span className="colon">:</span>
                      <span className="value">
                        {accountData?.updatedAt
                          ? ConvertUTCtoDateTime(accountData?.updatedAt)
                          : '-'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stations' && (
          <div className="facility-details-group">
            <div className="facility-button">
              <div className="section stations">
                <h4>{t('stations')}</h4>
                <div className="stats-grid">
                  <div className="stat-item no-of-stations">
                    <div className="value">
                      {accountData?.stationCount ??
                        accountData?.noOfStations ??
                        (accountData as any)?.totalStations ??
                        0}
                    </div>
                    <p className="label">{t('noOfStations')}</p>
                  </div>
                  <div className="stat-item no-of-chargers">
                    <div className="value">
                      {accountData?.totalDeviceCount ??
                        (accountData as any)?.noOfDevices ??
                        accountData?.noOfChargers ??
                        (accountData as any)?.totalChargers ??
                        0}
                    </div>
                    <p className="label">{t('noOfChargers')}</p>
                  </div>
                  <div className="stat-item online-chargers">
                    <div className="value">
                      {accountData?.activeDeviceCount ?? (accountData as any)?.onlineChargers ?? 0}
                    </div>
                    <p className="label">{t('onlineChargers')}</p>
                  </div>
                  <div className="stat-item kwh-consumed">
                    <div className="value">
                      {ToFixedValue(
                        accountData?.powerConsumed ?? (accountData as any)?.kwhConsumed,
                        3,
                      )}{' '}
                      kWh{' '}
                    </div>
                    <p className="label small">{t('kwhConsumedPaid')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="facility-details-group">
            <div className="facility-button">
              <div className="section documents">
                <h4>{t('documents')}</h4>
                <p className="doc-label">{t('governmentId')}</p>
                {editMode ? (
                  <CustomFileInput
                    acceptedFileType=".pdf,.jpg,.jpeg,.png"
                    placeholder={t('uploadHere')}
                    name="governmentId"
                    existingName="governmentIdOld"
                    selectedFiles={editData.governmentId}
                    existingFiles={editData.governmentIdOld}
                    onChangeValue={handleInputChange}
                    unselectHandler={handleRemoveFile}
                    maxFileSizeMB={1}
                    maxFileCount={1}
                    error={
                      editData.governmentId?.length > 0 &&
                      !checkFileSizeValidation(editData.governmentId, 1, 1)
                    }
                    errorMessage={t('Documentmustbe1mb')}
                  />
                ) : (
                  <div className="doc-list">
                    {(() => {
                      const govFiles = resolveGovernmentIdFiles((accountData as any)?.governmentId);
                      const governmentId = govFiles[0];
                      if (governmentId?.signedUrl || governmentId?.url) {
                        const fileUrl = governmentId.signedUrl || governmentId.url;
                        return (
                          <div className="doc-item">
                            <div className="doc-icon">
                              <DocumentFile />
                              <span>
                                {governmentId.fileName || governmentId.name || t('governmentId')}
                              </span>
                            </div>
                            <div
                              className="action"
                              role="button"
                              tabIndex={0}
                              onClick={() => openPdf(fileUrl)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  openPdf(fileUrl);
                                }
                              }}>
                              <EyeIcon />
                            </div>
                          </div>
                        );
                      }
                      if (governmentId?.fileName || governmentId?.name) {
                        return (
                          <div className="doc-item">
                            <DocumentFile />
                            <span>{governmentId.fileName || governmentId.name}</span>
                            <div className="action">{/* No action if no URL */}</div>
                          </div>
                        );
                      }
                      return <div className="no-docs">-</div>;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="facility-details-footer">
        {editMode ? (
          <div className="footer-actions-edit">
            <MyButton
              label={t('cancel')}
              buttonType="outline"
              onClickFunc={() => handleCancelEdit()}
            />
            <MyButton
              label={t('submit')}
              buttonType="submit"
              onClickFunc={handleEditSubmit}
              loading={submitLoading}
              disableButton={submitLoading}
            />
          </div>
        ) : (
          <>
            {accountData?.submission?.status === 'pending' && (
              <div className="footer-actions">
                <MyButton
                  label={t('reject')}
                  buttonType="error"
                  onClickFunc={handleReject}
                  disableButton={!permissionCheck('cpoPartnerReject')}
                />

                <MyButton
                  label={t('verify')}
                  buttonType="submit"
                  onClickFunc={handleVerify}
                  disableButton={!permissionCheck('cpoPartnerApprove')}
                />
              </div>
            )}
            <div className="footer-reset">
              <MyButton
                label={t('resetPassword')}
                buttonType="outline"
                onClickFunc={handleResetPassword}
                disableButton={!permissionCheck('cpoPartnerResetPassword')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FacilityDetailsSidePanel;
